// Section 22 Society tab (Stream D): settlement trust and happiness with their breakdowns, the
// inequality dial, norms in force with their counters, faction marks, the festival, pairings
// awaiting approval, refusals, childcare, and the education panel (schooling, subjects, seats,
// teachers, students, literacy, apprenticeships with the controls' candidates). Pure over
// (game, draft, cfg); numbers stay numbers and tooltips are explain() factor lists (24.5).
import {
  configSnapshot,
  evaluateTarget,
  type Config,
  type Decision,
  type Factor,
  type FestivalOption,
  type GameState,
  type JobId,
  type NormId,
  type PersonId,
  type PromptId,
  type SchoolSubject,
  type SchoolingOption,
  type Season,
  type SkillId,
  type TrustCause,
} from '../sim/api';
import { factorTooltip, tooltipVM, type TooltipVM } from './explain';
import { memo } from './memo';
import { stampOfTurn, type SeasonStamp } from './calendar';

let snapshot: Config | null = null;
function currentConfig(cfg: Config | undefined): Config {
  if (cfg !== undefined) return cfg;
  snapshot ??= configSnapshot();
  return snapshot;
}

export interface NormRow {
  id: NormId;
  established: boolean;
  count: number;
  needed: number;
  announced: boolean;
  since: SeasonStamp | null;
  /** Seasons until an established norm fades without reinforcement. */
  fadesIn: number | null;
}

export interface PairingRow {
  promptId: PromptId;
  people: { id: PersonId; name: string; ageYears: number; job: JobId }[];
  /** The draft's answer, if any. */
  answer: boolean | null;
}

export interface ApprenticeRow {
  apprenticeId: PersonId;
  apprentice: string;
  masterId: PersonId;
  master: string;
  job: JobId;
  apprenticeSkill: number;
  masterSkill: number;
  /** Working alongside a master of skill 4+ in the same job this season. */
  active: boolean;
  /** Changed in the draft (set or ended), not yet applied. */
  pending: 'set' | 'end' | null;
  gain: TooltipVM | null;
}

export interface CandidateRow {
  id: PersonId;
  name: string;
  job: JobId;
  skill: number;
}

export interface CoupleRow {
  motherId: PersonId;
  mother: string;
  father: string;
  pregnant: number | null;
  conception: TooltipVM;
}

export interface SocietyVM {
  trust: { value: number; tooltip: TooltipVM };
  happiness: { value: number; tooltip: TooltipVM };
  thresholds: { refusal: number; crime: number; bonus: number };
  inequality: { food: number; beds: number; series: number[] };
  norms: NormRow[];
  factions: { id: 'newcomers'; until: SeasonStamp }[];
  festival: {
    policy: FestivalOption;
    hall: boolean;
    last: SeasonStamp | null;
    activeSeasons: number;
    next: Season | null;
    food: number;
    wood: number;
  };
  pairings: PairingRow[];
  refusingHouseholds: number;
  uncoveredChildren: number;
  education: {
    schooling: SchoolingOption;
    subjects: [SchoolSubject, SchoolSubject];
    schools: number;
    seats: number;
    teachers: number;
    students: number;
    waiting: number;
    literacy: number;
    literacySeries: number[];
    subjectOptions: SchoolSubject[];
  };
  apprenticeships: ApprenticeRow[];
  masters: CandidateRow[];
  apprentices: CandidateRow[];
  couples: CoupleRow[];
}

/** Last season's trust entries as a breakdown of what moved settlement trust. */
function trustFactors(game: GameState, population: number): Factor[] {
  const report = game.history[game.history.length - 1];
  const rows = new Map<TrustCause, number>();
  for (const e of report?.entries ?? []) {
    if (e.kind !== 'trust' || population === 0) continue;
    rows.set(e.cause, (rows.get(e.cause) ?? 0) + (e.delta * e.people) / population);
  }
  return [...rows.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([cause, delta]) => ({
      source: `trust:${cause}`,
      op: 'add',
      value: delta,
      delta,
      note: cause,
    }));
}

/** The mean happiness target's factors over everyone ("why is the settlement unhappy"). */
function happinessFactors(game: GameState, cfg: Config): Factor[] {
  const ids = Object.keys(game.people).sort() as PersonId[];
  const sums = new Map<string, Factor>();
  for (const id of ids) {
    for (const f of evaluateTarget(game, { kind: 'happinessTarget', personId: id }, cfg).factors) {
      const row = sums.get(f.source);
      if (row === undefined) sums.set(f.source, { ...f, value: f.delta, delta: f.delta });
      else {
        row.value += f.delta;
        row.delta += f.delta;
      }
    }
  }
  return [...sums.values()].map((f) => ({
    ...f,
    value: f.value / ids.length,
    delta: f.delta / ids.length,
    note: f.op === 'base' ? 'base' : (f.source.split(':')[1] ?? f.source),
  }));
}

function nextFestival(policy: FestivalOption): Season | null {
  return policy === 'off' ? null : policy;
}

export const societyVM = memo(
  (game: GameState, draft: Decision | null, cfgIn?: Config): SocietyVM => {
    const cfg = currentConfig(cfgIn);
    const people = Object.values(game.people).sort((a, b) => (a.id < b.id ? -1 : 1));
    const pop = people.length;
    const last = game.history[game.history.length - 1];
    const trust = pop === 0 ? 0 : people.reduce((a, p) => a + p.trust, 0) / pop;
    const happiness = pop === 0 ? 0 : people.reduce((a, p) => a + p.happiness, 0) / pop;
    const spy = cfg.people.seasonsPerYear;
    const turnNow = game.turn;

    const norms: NormRow[] = (Object.keys(game.norms).sort() as NormId[]).map((id) => {
      const n = game.norms[id];
      return {
        id,
        established: n.established,
        count: n.count,
        needed: cfg.normRules.establishAfter,
        announced: n.announced,
        since: n.since === null ? null : stampOfTurn(n.since),
        fadesIn:
          n.established && n.lastReinforcedTurn !== null
            ? Math.max(0, cfg.normRules.decayAfter - (turnNow - n.lastReinforcedTurn))
            : null,
      };
    });

    const hall = Object.values(game.buildings).some(
      (b) => b.type === 'common-hall' && b.condition > 0,
    );
    const until = game.festival.activeUntilTurn;
    const festival = {
      policy: game.policies.festival,
      hall,
      last: game.festival.lastHeldTurn === null ? null : stampOfTurn(game.festival.lastHeldTurn),
      activeSeasons: until === null ? 0 : Math.max(0, until - turnNow),
      next: nextFestival(game.policies.festival),
      food: cfg.festival.foodPerPerson * pop,
      wood: cfg.festival.wood,
    };

    const answers = draft?.eventChoices ?? {};
    const pairings: PairingRow[] = game.openPrompts.flatMap((prompt) => {
      if (prompt.kind !== 'pairing') return [];
      const answer = answers[prompt.id];
      return [
        {
          promptId: prompt.id,
          people: prompt.personIds.flatMap((id) => {
            const p = game.people[id];
            return p === undefined
              ? []
              : [{ id, name: p.name, ageYears: Math.floor(p.ageSeasons / spy), job: p.job }];
          }),
          answer: answer?.kind === 'pairing' ? answer.approve : null,
        },
      ];
    });

    const refusing =
      game.counters.lastRefusalTurn === turnNow
        ? (game.counters.refusingHouseholds ?? []).length
        : 0;
    let uncovered = 0;
    for (const e of last?.entries ?? [])
      if (e.kind === 'warning' && e.code === 'children-uncovered') uncovered = e.amount ?? 0;

    const schools = Object.values(game.buildings).filter((b) => b.type === 'school');
    const seats = Math.floor(
      schools.reduce(
        (a, b) =>
          a +
          cfg.buildings.school.seats * Math.min(1, b.condition / cfg.building.fullEffectCondition),
        0,
      ) + 1e-9,
    );
    let waiting = 0;
    for (const e of last?.entries ?? []) if (e.kind === 'school') waiting = e.waiting;
    const skills = (Object.keys(cfg.jobs) as JobId[]).filter((j): j is SkillId => j !== 'rest');

    const pendingApprentice = draft?.apprenticeships ?? {};
    const masterOf = new Map<PersonId, PersonId>();
    for (const p of people) if (p.masterId !== null) masterOf.set(p.id, p.masterId);
    const pendingIds = new Set<PersonId>();
    for (const [id, m] of Object.entries(pendingApprentice) as [PersonId, PersonId | null][]) {
      pendingIds.add(id);
      if (m === null) continue;
      masterOf.set(id, m);
    }
    const apprenticeships: ApprenticeRow[] = [];
    for (const [id, masterId] of [...masterOf.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
      const a = game.people[id];
      const m = game.people[masterId];
      if (a === undefined || m === undefined) continue;
      const job = draft?.assignments[masterId] ?? m.job;
      const ajob = draft?.assignments[id] ?? a.job;
      const skill = job === 'rest' ? 0 : (m.skills[job] ?? 0);
      const active = job !== 'rest' && ajob === job && skill >= cfg.skills.masterMinSkill;
      const ending = pendingApprentice[id] === null;
      apprenticeships.push({
        apprenticeId: id,
        apprentice: a.name,
        masterId,
        master: m.name,
        job,
        apprenticeSkill: job === 'rest' ? 0 : (a.skills[job] ?? 0),
        masterSkill: skill,
        active,
        pending: ending ? 'end' : pendingIds.has(id) ? 'set' : null,
        gain:
          job === 'rest'
            ? null
            : tooltipVM(
                game,
                {
                  kind: 'skillGain',
                  personId: id,
                  skill: job,
                  via: 'apprentice',
                  teacherId: masterId,
                },
                cfg,
              ),
      });
    }
    const taken = new Map<PersonId, number>();
    for (const row of apprenticeships)
      if (row.pending !== 'end') taken.set(row.masterId, (taken.get(row.masterId) ?? 0) + 1);
    const masters: CandidateRow[] = people
      .filter(
        (p) =>
          p.job !== 'rest' &&
          (p.skills[p.job] ?? 0) >= cfg.skills.masterMinSkill &&
          (taken.get(p.id) ?? 0) < cfg.skills.maxApprentices,
      )
      .map((p) => ({
        id: p.id,
        name: p.name,
        job: p.job,
        skill: p.job === 'rest' ? 0 : (p.skills[p.job] ?? 0),
      }));
    const apprentices: CandidateRow[] = people
      .filter((p) => p.ageSeasons >= cfg.people.adultAtSeasons && !masterOf.has(p.id))
      .map((p) => ({
        id: p.id,
        name: p.name,
        job: p.job,
        skill: p.job === 'rest' ? 0 : (p.skills[p.job] ?? 0),
      }));

    const couples: CoupleRow[] = [];
    for (const p of people) {
      if (p.sex !== 'female' || p.partnerId === null) continue;
      const years = p.ageSeasons / spy;
      if (
        years < cfg.lifecycle.conceptionMinAgeYears ||
        years >= cfg.lifecycle.conceptionMaxAgeYears + 1
      )
        continue;
      const partner = game.people[p.partnerId];
      if (partner === undefined) continue;
      couples.push({
        motherId: p.id,
        mother: p.name,
        father: partner.name,
        pregnant: p.status.pregnant?.seasonsRemaining ?? null,
        conception: tooltipVM(
          game,
          { kind: 'conception', householdId: p.householdId, motherId: p.id },
          cfg,
        ),
      });
    }

    return {
      trust: { value: trust, tooltip: factorTooltip(trust, trustFactors(game, pop)) },
      happiness: {
        value: happiness,
        tooltip: factorTooltip(happiness, happinessFactors(game, cfg)),
      },
      thresholds: {
        refusal: cfg.trust.refusalBelow,
        crime: cfg.trust.crimeAndEmigrationBelow,
        bonus: cfg.trust.bonusAbove,
      },
      inequality: {
        food: last?.gini.food ?? 0,
        beds: last?.gini.beds ?? 0,
        series: game.series.gini.slice(-12),
      },
      norms,
      factions: game.factionMarks
        .filter((f) => f.untilTurn >= turnNow)
        .map((f) => ({ id: f.id, until: stampOfTurn(f.untilTurn) })),
      festival,
      pairings,
      refusingHouseholds: refusing,
      uncoveredChildren: uncovered,
      education: {
        schooling: game.policies.schooling,
        subjects: game.policies.schoolSubjects,
        schools: schools.length,
        seats,
        teachers: people.filter((p) => p.job === 'teacher').length,
        students: people.filter((p) => p.status.inSchool).length,
        waiting,
        literacy: pop === 0 ? 0 : people.filter((p) => p.status.literate).length / pop,
        literacySeries: game.series.literacy.slice(-12),
        subjectOptions: [...skills, 'literacy'],
      },
      apprenticeships,
      masters,
      apprentices,
      couples,
    };
  },
);
